/** @jsx jsx */
import {jsx} from '@emotion/core'

import * as React from 'react'
import Tooltip from '@reach/tooltip'
import {FaSearch, FaTimes} from 'react-icons/fa'
import * as colors from 'styles/colors'
import {useBookSearch, useRefetchBookSearchQuery} from 'utils/books'
import {BookRow} from 'components/book-row'
import {BookListUL, Spinner, Input} from 'components/lib'
import {Profiler} from 'components/profiler'

interface FormData extends HTMLFormControlsCollection {
	search: HTMLInputElement
}
interface FormElement extends HTMLFormElement {
	readonly elements: FormData
}

export function DiscoverBooksScreen() {
	const [query, setQuery] = React.useState('')
	const [queried, setQueried] = React.useState(false)
	const {books, error, isLoading, isError, isSuccess} = useBookSearch(query)
	const refetchBookSearchQuery = useRefetchBookSearchQuery()

	/* This "clean up" effect serves its purpose to reload a new list of books
	 * for the "Discovery" page (in the background)
	 * right AFTER the server has updated the "Reading List"
	 * (i.e. the user added/removed some books to/from the "Reading List")
	 * Without this effect, the next time the user visit the "Discovery" page,
	 * they will see a jump in the book list due to the effect of updating
	 * the list only when they visit the "Discover" page.
	 */
	React.useEffect(() => {
		// The ReturnType of useEffect() clean up function is `void`
		// and the call `refetchBookSearchQuery()` returns a promise
		// that's why we wrap it with an IIFE to please TS :))
		return () =>
			(function cleanUp() {
				refetchBookSearchQuery()
			})()
	}, [refetchBookSearchQuery])

	function handleSearchSubmit(event: React.FormEvent<FormElement>) {
		event.preventDefault()
		setQueried(true)
		setQuery(event.currentTarget.elements.search.value)
	}

	return (
		<div>
			<div>
				<form onSubmit={handleSearchSubmit}>
					<Input
						placeholder="Search books..."
						id="search"
						type="search"
						css={{width: '100%'}}
					/>
					<Tooltip label="Search Books">
						<label htmlFor="search">
							<button
								type="submit"
								css={{
									border: '0',
									position: 'relative',
									marginLeft: '-35px',
									background: 'transparent',
								}}
							>
								{isLoading ? (
									<Spinner />
								) : isError ? (
									<FaTimes
										aria-label="error"
										css={{color: colors.danger}}
									/>
								) : (
									<FaSearch aria-label="search" />
								)}
							</button>
						</label>
					</Tooltip>
				</form>
				{isError ? (
					<div css={{color: colors.danger}}>
						<p>There was an error:</p>
						<pre>{(error as Error).message}</pre>
					</div>
				) : null}
			</div>
			<div>
				{queried ? null : (
					<div
						css={{
							marginTop: 20,
							fontSize: '1.2em',
							textAlign: 'center',
						}}
					>
						<p>Welcome to the discover page.</p>
						<p>Here, let me load a few books for you...</p>
						{isLoading ? (
							<div css={{width: '100%', margin: 'auto'}}>
								<Spinner />
							</div>
						) : isSuccess && books.length ? (
							<p>
								Here you go! Find more books with the search bar
								above.
							</p>
						) : isSuccess && !books.length ? (
							<p>
								Hmmm... I couldn't find any books to suggest for
								you. Sorry.
							</p>
						) : null}
					</div>
				)}
				{books.length ? (
					<Profiler
						id="Discover Books Screen Book List"
						metadata={{query, bookCount: books.length}}
					>
						<BookListUL css={{marginTop: 20}}>
							{books.map(book => (
								<li key={book.id} aria-label={`${book.title}`}>
									<BookRow key={book.id} book={book} />
								</li>
							))}
						</BookListUL>
					</Profiler>
				) : queried ? (
					<div
						css={{
							marginTop: 20,
							fontSize: '1.2em',
							textAlign: 'center',
						}}
					>
						{isLoading ? (
							<div css={{width: '100%', margin: 'auto'}}>
								<Spinner />
							</div>
						) : (
							<p>
								Hmmm... I couldn't find any books with the query
								"{query}." Please try another.
							</p>
						)}
					</div>
				) : null}
			</div>
		</div>
	)
}
